/** The loading yard as the deterministic stealth field understands it. */

import {
  bakeLightGrid,
  type Aabb,
  type CoverVolume,
  type LightEmitter,
  type LightGrid,
} from '@/systems/stealth';
import {
  BIN_ROW,
  PALLET_STACKS,
  YARD_DAWN_BIN_ROW,
  yardDawnLighting,
  RACE_GATE_AT,
  TRAILER_HEIGHT,
  TRAILER_SHADOW_REGION,
  YARD,
  loadingYardFixtures,
  loadingYardSolids,
  palletStackFootprint,
} from '@/world/loadingYard';

/** The field samples the wet apron itself, not an arbitrary eye-height plane. */
const SAMPLE_Y = YARD.floorY;
const OCCLUDER_MIN_TOP = SAMPLE_Y + 0.05;

function solidToAabb(half: { x: number; y: number; z: number }, pos: { x: number; y: number; z: number }): Aabb {
  return {
    min: { x: pos.x - half.x, y: pos.y - half.y, z: pos.z - half.z },
    max: { x: pos.x + half.x, y: pos.y + half.y, z: pos.z + half.z },
  };
}

const YARD_OCCLUDER_PREFIXES = [
  'shed-wall',
  'truck-cab-solid',
  'pallet-stack-solid',
  'race-run-solid',
  'bin-row-solid',
] as const;

/**
 * The trailer, at FULL height, and it is deliberately not read off the solids.
 *
 * Every other occluder in this room is its collider, which is the property that
 * stops what you see and what blocks light from drifting apart. The trailer is
 * the one exception and it has to be: since session 27 its physical box starts
 * at the deck's underside so that she can go **under** it — that is the beat's
 * last stage — and a light grid built from that box would let a headlight 1 m
 * off the ground shine straight through the gap and light the one square metre
 * of yard the whole beat walks toward.
 *
 * Light does not go under a trailer. The eight metres of steel over her head is
 * what makes that space dark, and it is still eight metres of steel whatever
 * her body is allowed to walk through. So this is derived from `TRAILER_AT` and
 * the asset's own footprint, from the ground up.
 *
 * The concealment half of the same shelter is `TRAILER_SHADOW_REGION` in
 * `yardCover()`; the two are different volumes on purpose (see `TRAILER_LOCAL`).
 */
function trailerOccluder(): Aabb {
  return {
    min: { x: TRAILER_SHADOW_REGION.minX, y: 0, z: TRAILER_SHADOW_REGION.minZ },
    max: { x: TRAILER_SHADOW_REGION.maxX, y: TRAILER_HEIGHT, z: TRAILER_SHADOW_REGION.maxZ },
  };
}

/** The trailer, cab, five stacks, two race runs, bins and shed are baked from room solids. */
export function yardOccluders(options: { dawn?: boolean } = {}): Aabb[] {
  const occluders: Aabb[] = [trailerOccluder()];
  for (const solid of loadingYardSolids(options)) {
    if (!YARD_OCCLUDER_PREFIXES.some((prefix) => solid.name.startsWith(prefix))) continue;
    const box = solidToAabb(solid.half, solid.pos);
    if (box.max.y < OCCLUDER_MIN_TOP) continue;
    occluders.push(box);
  }
  return occluders;
}

/** The stealth grid reads the renderer's four fixtures rather than maintaining a second table. */
export function yardEmitters(): LightEmitter[] {
  return loadingYardFixtures().map((fixture) => ({
    kind: 'point' as const,
    position: { ...fixture.position },
    radius: fixture.range,
    intensity: 1,
  }));
}

/** Cover is the entire authored route: trailer, each stack, bins, then the race's outer kick-board. */
export function yardCover(options: { dawn?: boolean } = {}): CoverVolume[] {
  // Read off the ASSET, not typed again. These were 1.28 and 1.08 as literals —
  // the pallet stack's footprint copied into a second file — and the collider in
  // `loadingYard.ts` already reads the real thing. Two statements of one
  // dimension agree until somebody re-proportions the asset, and then the cover
  // volume is silently the wrong size in a room whose whole mechanic is which
  // cover to move to next. `CULL_RAIL.y` is this project's standing example.
  const palletFootprint = palletStackFootprint();
  const palletHalfX = palletFootprint.width / 2;
  const palletHalfZ = palletFootprint.length / 2;
  const cover: CoverVolume[] = [
    {
      min: { x: TRAILER_SHADOW_REGION.minX, y: 0, z: TRAILER_SHADOW_REGION.minZ },
      max: { x: TRAILER_SHADOW_REGION.maxX, y: 0.8, z: TRAILER_SHADOW_REGION.maxZ },
      concealment: 0.75,
    },
    ...PALLET_STACKS.map((stack) => ({
      min: { x: stack.x - palletHalfX, y: 0, z: stack.z - palletHalfZ },
      max: { x: stack.x + palletHalfX, y: 1, z: stack.z + palletHalfZ },
      concealment: 0.7,
    })),
  ];

  const binRow = options.dawn === true ? YARD_DAWN_BIN_ROW : BIN_ROW;
  const firstBin = binRow[0];
  const lastBin = binRow[binRow.length - 1];
  if (firstBin === undefined || lastBin === undefined) throw new Error('loading yard stealth: bin row missing');
  cover.push(
    {
      min: { x: firstBin.x - 0.36, y: 0, z: firstBin.z - 0.31 },
      max: { x: lastBin.x + 0.36, y: 1, z: lastBin.z + 0.31 },
      concealment: 0.7,
    },
    {
      min: { x: 9, y: 0, z: YARD.minZ },
      max: { x: 9.7, y: 0.6, z: RACE_GATE_AT.z },
      concealment: 0.45,
    },
  );
  return cover;
}

/** Bake the roofless zone once; sampling this grid during play remains O(1). */
export function bakeYardLightGrid(): LightGrid {
  const grid = bakeLightGrid(
    {
      minX: YARD.minX,
      minZ: YARD.minZ,
      width: YARD.maxX - YARD.minX,
      depth: YARD.maxZ - YARD.minZ,
      sampleY: SAMPLE_Y,
    },
    yardEmitters(),
    yardOccluders(),
  );

  return {
    ...grid,
    sample: (x: number, z: number): number => Math.min(1, grid.sample(x, z)),
  };
}

/**
 * The dawn's light floor: what the open yard is lit to with every fixture off.
 *
 * The storm yard's grid is its fixtures and nothing else, because at night in
 * rain the space between the lamps is dark. At dawn it is not: the sky is up
 * and the whole yard is in a flat grey light, so a pig in the open is visible
 * with the floods out — less, not never. 0.3 sits under the flood pools
 * (which reach 1) and above a stack's shadow once cover takes its share, so
 * the floods going out is a real change in how far she can be seen and the
 * pallets still mean something. Measured as a delta by `beat-12-dawn.spec.ts`.
 */
export const YARD_DAWN_DAYLIGHT = 0.5;

/**
 * Beat 12's two fields: the yard lit by the race flood and the shed lamp, and
 * the yard after mark 1. Both carry the daylight floor. The room module
 * samples one or the other off its own `lightsOut`, which is simulation
 * state — never off the lamps' intensities, which fade on the render step.
 */
export function bakeYardDawnGrids(): { readonly lit: LightGrid; readonly dark: LightGrid } {
  const bounds = {
    minX: YARD.minX,
    minZ: YARD.minZ,
    width: YARD.maxX - YARD.minX,
    depth: YARD.maxZ - YARD.minZ,
    sampleY: SAMPLE_Y,
  };
  const occluders = yardOccluders({ dawn: true });
  const floods = bakeLightGrid(bounds, (yardDawnLighting().fixtures ?? []).map((fixture) => ({
    kind: 'point' as const,
    position: { ...fixture.position },
    radius: fixture.range,
    intensity: 1,
  })), occluders);
  // `values` agrees with `sample` in both, so nothing that reads the raw field
  // (the storm yard's grid carries a comment about exactly that hole) can see
  // the floods in the dark one.
  const withFloor = (grid: LightGrid | null): LightGrid => ({
    ...floods,
    values: grid === null
      ? new Float32Array(floods.values.length).fill(YARD_DAWN_DAYLIGHT)
      : floods.values.map((value) => Math.min(1, value + YARD_DAWN_DAYLIGHT)),
    sample: (x: number, z: number): number => Math.min(1, YARD_DAWN_DAYLIGHT + (grid === null ? 0 : grid.sample(x, z))),
  });
  return { lit: withFloor(floods), dark: withFloor(null) };
}

